<template>
  <div id="app">
    <transition name="chargement-fondu">
      <div
        v-if="pageEnChargement"
        class="chargementPage"
        role="status"
        aria-live="polite"
        aria-label="Chargement de la page"
      >
        <div class="chargementPageContenu" aria-hidden="true">
          <div class="chargementCarre chargementCarreGrand"></div>
          <div class="chargementLignes">
            <div class="chargementCarre chargementLigne"></div>
            <div class="chargementCarre chargementLigne chargementLigneCourte"></div>
            <div class="chargementCarre chargementLigne"></div>
          </div>
        </div>
        <span class="srOnly">Chargement de la page…</span>
      </div>
    </transition>

    <router-view :key="$route.fullPath" />
  </div>
</template>

<script>
export default {
  name: "App",

  data() {
    return {
      pageEnChargement: false,
      delaiAffichage: null,
      delaiMasquage: null,
      retirerAvantNavigation: null,
      retirerApresNavigation: null,
      lastCheck: 0
    };
  },

  mounted() {
    this.checkVersion();
    document.addEventListener("click", this.checkVersion);

    // Les gardes sont enregistrées après l'arrivée sur le site :
    // le chargement global ne s'affiche donc jamais sur la première page.
    this.retirerAvantNavigation = this.$router.beforeEach((to, from, next) => {
      this.preparerChargementPage();
      next();
    });

    this.retirerApresNavigation = this.$router.afterEach(() => {
      this.terminerChargementPage();
    });
  },

  beforeUnmount() {
    window.clearTimeout(this.delaiAffichage);
    window.clearTimeout(this.delaiMasquage);
    document.removeEventListener("click", this.checkVersion);
    this.retirerAvantNavigation?.();
    this.retirerApresNavigation?.();
  },

  methods: {
    checkVersion() {
      const now = Date.now();

      // Ne vérifie pas plus d'une fois toutes les 20 secondes.
      if (now - this.lastCheck < 20000) return;

      this.lastCheck = now;

      fetch(`/meta.json?v=${now}`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          return response.json();
        })
        .then(meta => {
          const currentVersion = meta.version;
          const localVersion = localStorage.getItem("app_version");

          if (localVersion && currentVersion !== localVersion) {
            localStorage.setItem("app_version", currentVersion);
            window.location.reload();
            return;
          }

          if (!localVersion && currentVersion) {
            localStorage.setItem("app_version", currentVersion);
          }
        })
        .catch(error => {
          console.error("Erreur de récupération meta.json :", error);
        });
    },

    preparerChargementPage() {
      window.clearTimeout(this.delaiAffichage);
      window.clearTimeout(this.delaiMasquage);

      // Une navigation rapide ne déclenche aucun écran de chargement.
      this.delaiAffichage = window.setTimeout(() => {
        this.pageEnChargement = true;
      }, 250);
    },

    terminerChargementPage() {
      window.clearTimeout(this.delaiAffichage);

      if (!this.pageEnChargement) return;

      // Une fois visible, le skeleton reste brièvement affiché pour éviter
      // une disparition brutale.
      this.delaiMasquage = window.setTimeout(() => {
        this.pageEnChargement = false;
      }, 250);
    }
  }
};
</script>

<style>
.chargementPage {
  position: fixed;
  z-index: 99999;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.96);
}

.chargementPageContenu {
  display: grid;
  grid-template-columns: 110px minmax(180px, 320px);
  gap: 1.25rem;
  width: min(100%, 470px);
  padding: 1.25rem;
  background: #fff;
  border: 1px solid #e4e4e7;
  border-radius: 6px;
}

.chargementCarre {
  position: relative;
  overflow: hidden;
  background: #e5e7eb;
  border-radius: 4px;
}

.chargementCarre::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.8),
    transparent
  );
  transform: translateX(-100%);
  animation: chargementBrillance 1.35s ease-in-out infinite;
}

.chargementCarreGrand {
  width: 110px;
  height: 110px;
}

.chargementLignes {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.9rem;
}

.chargementLigne {
  width: 100%;
  height: 1rem;
}

.chargementLigneCourte {
  width: 62%;
}

.chargement-fondu-enter-active,
.chargement-fondu-leave-active {
  transition: opacity 180ms ease;
}

.chargement-fondu-enter-from,
.chargement-fondu-leave-to {
  opacity: 0;
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes chargementBrillance {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 520px) {
  .chargementPageContenu {
    grid-template-columns: 82px minmax(0, 1fr);
    gap: 1rem;
  }

  .chargementCarreGrand {
    width: 82px;
    height: 82px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chargementCarre::after {
    animation: none;
  }
}
</style>
